// src/hooks/useSanitizedInput.js
import { useState, useCallback } from 'react';

/**
 * Sanitize input on frontend before sending to backend
 */
const sanitizeInput = (value, type = 'text') => {
  if (value === null || value === undefined) return '';

  let sanitized = String(value); // ❌ no trim here

  switch (type) {
    case 'email':
      sanitized = sanitized
        .toLowerCase()
        .trim() // ✅ trim only email
        .replace(/[<>'"]/g, '');
      break;

    case 'mrcode':
      sanitized = sanitized
        .trim() // ✅ trim MR-Code
        .replace(/[^A-Za-z0-9-]/g, '')
        .toUpperCase();
      break;

    case 'phone':
      sanitized = sanitized
        .trim() // ✅ trim phone
        .replace(/[^0-9+]/g, '');
      break;

    case 'number': {
      const num = parseFloat(sanitized);
      return isNaN(num) ? '' : num;
    }

    case 'alphanumeric':
      // Allow spaces inside text
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s,.'-]/g, '');
      break;

    case 'text':
    default:
      // 🔒 Prevent XSS but keep spaces
      sanitized = sanitized.replace(
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        ''
      );
      sanitized = sanitized.replace(/[<>]/g, '');
      break;
  }

  return sanitized;
};

export const useSanitizedInput = (initialValue = '', type = 'text') => {
  const [value, setValue] = useState(initialValue);
  
  // ✅ Handle change from event object
  const handleChange = useCallback((e) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeInput(rawValue, type);
    setValue(sanitized);
  }, [type]);
  
  // ✅ Direct setter for programmatic updates (without event)
  const setValueDirect = useCallback((newValue) => {
    const sanitized = sanitizeInput(newValue, type);
    setValue(sanitized);
  }, [type]);
  
  // ✅ Reset function
  const reset = useCallback(() => setValue(''), []);
  
  return [value, handleChange, reset, setValueDirect];
};